import i18next from 'i18next'
import React from 'react'
import { InView } from 'react-intersection-observer'
import { Link } from 'widgets-for-react'
import { UserType } from '../../../subscription/subscriptionApi'
import {
  ButtonGhost,
  ButtonPrimary,
  StartTrialButton,
} from '../../../utils/buttons'
import { DataLayer } from '../../../utils/datalayer'
import { blockInviewHandler } from '../../../utils/inview'
import { link, linkWrapper } from '../../../utils/links'
import { CurrentPage } from '../../../utils/routes'
import { SVGIcon } from '../../../utils/svgicon'
import { convertRemToPx, slugify } from '../../../utils/utils'
import { Pages, routeUpdaters } from '../routes/routesState'
import { SearchTab } from '../search/searchState'
import { SearchBarLayout } from '../searchbar/searchBar.layout'

// ----- Header Layout

type HeaderLayoutProps = {
  mobileMenuMode: 'closed' | 'open'
  languageSwitchMode: 'closed' | 'open'
  setMobileMenuMode: (_: 'closed' | 'open') => void
  setLanguageSwitchMode: (_: 'closed' | 'open') => void
  searchString: string
  setSearchString: (_: string) => void
  searchTab: SearchTab
  isLoggedIn: boolean
  pageKind: CurrentPage<Pages>['kind']
  userName: string
  userType: UserType
  onLanguageChanged: () => void
}

export const HeaderLayout = (props: HeaderLayoutProps) => {
  const {
    isLoggedIn,
    languageSwitchMode,
    mobileMenuMode,
    setMobileMenuMode,
    setLanguageSwitchMode,
    searchString,
    setSearchString,
    searchTab: searchCategory,
    pageKind,
    userName,
    userType,
  } = props
  const isMenuOpen =
    window.innerWidth > convertRemToPx(60) || mobileMenuMode === 'open'
  const toggleMobileMenu = () => {
    document.documentElement.classList.toggle('nav--toggled')
    setMobileMenuMode(mobileMenuMode == 'open' ? 'closed' : 'open')
  }
  const closeMobileMenu = () => {
    document.documentElement.classList.remove('modal--toggled')
    document.documentElement.classList.remove('nav--toggled')
    setMobileMenuMode('closed')
  }
  const closeLangSwitch = () => setLanguageSwitchMode('closed')
  const isStudent = userType == 'student'
  const isTeacher = userType == 'teacher'
  const showSearchBar = !(pageKind == 'home' && isLoggedIn)
  return (
    <>
      <SkipLinks />
      <InView
        as="header"
        threshold={0}
        triggerOnce={true}
        onChange={(isInView, entry) =>
          blockInviewHandler(isInView, entry.target)
        }
        className={`header header--${pageKind} header--${
          isLoggedIn ? 'logged-in' : 'logged-out'
        } ${
          isLoggedIn
            ? userType === 'trial'
              ? 'header--trial'
              : userType === 'pro'
              ? 'header--pro'
              : userType === 'expired'
              ? 'header--expired'
              : ''
            : ''
        }`}
      >
        <div className="header__inner">
          <div className="header__toolbar">
            <Link
              className="header__logo"
              to={routeUpdaters(i18next.language).home.url}
              onClick={() => {
                closeMobileMenu()
                closeLangSwitch()
              }}
              title={i18next.t('a11y:navigateHome')}
            >
              <span className="sr-text">
                {i18next.t('a11y:navigateHome')}
              </span>
              <svg
                className="header__image"
                viewBox="0 0 556 106"
                width="556"
                height="106"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  className="header__image-text"
                  d="m23.8133736 49.312363c5.6899584 0 10.2961152-2.9017667 11.6508672-7.3556413v6.8157777h10.5670656v-19.0976741c0-3.5091133-2.5740288-7.4231242-7.3833984-7.4231242h-13.3443072v8.7727832h8.4672c.677376 0 1.0838016.4723806 1.0838016 1.214693 0 3.3066644-5.2835328 7.4906072-10.3638528 7.4906072-7.112448 0-12.870144-5.8710165-12.870144-14.5763167s5.757696-13.9014871 13.0733568-13.9014871c5.6222208 0 9.0768384 2.9017667 9.6864768 7.4906071h11.7863424c-.4064256-10.52733975-9.0091008-17.74801512-21.337344-17.74801512-12.870144 0-24.5887488 8.03047074-24.5887488 24.15889512 0 16.1284245 11.3121792 24.1588952 23.5726848 24.1588952zm38.2040064 0c4.402944 0 7.8575616-1.8895225 8.7381504-4.7238063v4.1839427h11.1089664v-21.8644749c0-7.693056-6.6382848-12.6867941-15.1732224-12.6867941-10.0929024 0-15.3086976 5.7360505-15.579648 12.6193111h9.7542144c.0677376-2.4293861 1.4224896-4.7238063 5.1480576-4.7238063 3.048192 0 4.741632 1.4171419 4.741632 3.4416303 0 1.6195908-.9483264 2.8342838-2.709504 2.8342838h-4.8771072c-7.1801856 0-12.0572928 4.0489769-12.0572928 10.7297886 0 5.6685676 4.5384192 10.1899251 10.9057536 10.1899251zm3.1159296-7.760539c-1.9643904 0-3.4546176-1.214693-3.4546176-2.9692496 0-2.0244885 1.4224896-3.4416304 3.38688-3.4416304h5.6899584v.2699318c0 3.1716986-2.032128 6.1409482-5.6222208 6.1409482zm35.1558144 7.760539c8.263987 0 13.615258-4.3863916 13.615258-11.0672034 0-15.2511461-18.0859396-7.8955048-18.0859396-13.9014871 0-1.7545567 1.4224896-2.631835 3.5223552-2.631835 2.3030784 0 4.2674684 1.6195908 4.6061564 3.914011h9.483264c-.27095-6.8832607-6.028646-11.4046181-14.5635836-11.4046181-6.8414976 0-12.2605056 4.4538745-12.2605056 10.5948227 0 14.103936 17.6795132 8.3678855 17.6795132 13.9014872 0 1.4846248-1.625702 2.4293861-3.996518 2.4293861-2.8449792 0-4.9448448-1.6195908-5.2835328-4.0489769h-9.4155264c.338688 7.4231242 6.1641216 12.2144135 14.6990592 12.2144135zm42.403738-.5398636v-9.1776808h-4.131994c-2.912717 0-4.470682-1.349659-4.470682-4.4538746v-11.2021692h8.602676v-9.1776809h-8.602676v-9.65006143h-11.176704v9.65006143h-6.299596v9.1776809h6.299596v11.7420328c0 8.4353684 5.554484 13.0916918 13.953946 13.0916918zm15.918336 0v-17.2081516c0-4.7912892 2.167603-7.625573 5.893171-7.625573h5.960909v-9.1776809h-3.590093c-3.793306 0-6.299597 1.0797272-8.263987 5.128704v-5.128704h-11.176704v34.0114055zm32.378572.5398636c10.634804 0 18.289152-7.4906072 18.289152-17.5455663 0-10.0549592-7.654348-17.5455663-18.289152-17.5455663-10.634803 0-18.289152 7.4906071-18.289152 17.5455663 0 10.0549591 7.654349 17.5455663 18.289152 17.5455663zm0-9.4476126c-4.267468 0-7.112448-3.2391815-7.112448-8.0979537s2.84498-8.0979537 7.112448-8.0979537c4.267469 0 7.112448 3.2391815 7.112448 8.0979537s-2.844979 8.0979537-7.112448 8.0979537zm31.701197 8.907749v-18.5578105c0-4.9937381 3.251405-8.6378173 7.925299-8.6378173 4.267469 0 7.112448 3.2391815 7.112448 7.8955049v19.3001229h8.331725v-21.0546796c0-7.9629878-5.012582-13.4965894-12.734669-13.4965894-4.877107 0-8.73815 2.0919713-10.634803 5.8035334v-5.2636699h-8.331725v34.0114055zm46.874419.5398636c10.363853 0 17.814989-7.4231242 17.814989-17.5455663s-7.451136-17.5455663-17.814989-17.5455663c-10.43159 0-17.882726 7.4231242-17.882726 17.5455663s7.451136 17.5455663 17.882726 17.5455663zm0-7.1531924c-5.689958 0-9.483264-4.2514257-9.483264-10.3923739s3.793306-10.3923739 9.483264-10.3923739c5.622221 0 9.415527 4.2514257 9.415527 10.3923739s-3.793306 10.3923739-9.415527 10.3923739zm31.768935 6.6133288v-19.5025718c0-4.7912892 2.777241-7.9629878 7.180185-7.9629878 3.996519 0 6.50281 3.1716986 6.50281 7.9629878v19.5025718h8.263987v-19.5025718c0-4.7912892 2.777242-7.9629878 7.247923-7.9629878 3.996519 0 6.50281 3.1716986 6.50281 7.9629878v19.5025718h8.263987v-21.9994408c0-7.2206754-4.741632-12.5518282-12.12503-12.5518282-5.419008 0-9.754215 2.9692496-11.312179 6.4783629-1.490228-4.1839427-5.215796-6.4783629-10.634804-6.4783629-4.809369 0-8.196249 2.4293861-9.889689 5.8710164v-5.3311529h-8.331725v34.0114055zm60.015513-39.3425583v-9.24516379h-8.941363v9.24516379zm-.27095 39.3425583v-34.0114055h-8.331725v34.0114055zm13.27657 0 7.993036-11.3371351 7.993037 11.3371351h8.805888l-12.328243-17.7480151 11.447654-16.2633904h-9.144576l-6.77376 9.7850274-6.841497-9.7850274h-9.076839l11.379917 16.2633904-12.328243 17.7480151zm41.523148.5398636c7.993037 0 13.208832-4.1839427 13.208832-10.5948227 0-15.386112-18.966528-7.2881584-18.966528-14.6437996 0-2.2944202 1.964391-3.6440792 4.944845-3.6440792 2.641767 0 5.554483 1.6195908 6.028647 4.7912893h7.586611c-.338688-6.6133289-5.825434-10.9997204-13.818471-10.9997204-7.04471 0-12.260505 4.1839427-12.260505 10.1224421 0 14.3738678 18.695577 7.1531924 18.695577 14.7112825 0 2.0244884-2.167603 3.6440792-5.419008 3.6440792-3.725568 0-6.231859-2.0244885-6.570547-5.2636699h-7.586611c.406426 7.2881583 5.893171 11.8769987 14.157158 11.8769987z"
                  fill="#151619"
                  fillRule="nonzero"
                  transform="translate(125.977039 25.757774)"
                />
                <g className="header__image-icon">
                  <path
                    d="m45.7080455.0001912-45.70842935 26.2892756 22.85459855 22.1266167 22.8538308 4.1636149.1439424-.0822168v-52.41507367z"
                    fill="#FFDBCD"
                  />
                  <path
                    d="m0 78.8703123 26.7104526-6.3562581 18.9979767-19.9339734-45.7084293-26.2902316z"
                    fill="#FFB79C"
                  />
                  <path
                    d="m0 78.8703123.28884442.1663455 45.41958488 26.1238862.1439424-.082217 7.5685335-32.5642728-7.5685335-19.8508007-.1439424-.0831727z"
                    fill="#FF946A"
                  />
                  <path
                    d="m45.8522758 105.078327 45.5625676-26.2070587-22.7812838-20.8553355-22.7812838-5.3526793z"
                    fill="#FF7039"
                  />
                  <path
                    d="m45.8522758 52.4979596v.1653895l45.5625676 26.2061028v-52.5785511z"
                    fill="#FF4C07"
                  />
                </g>
              </svg>
            </Link>

            <HeaderButtons
              screenSize="mobile"
              isLoggedIn={isLoggedIn}
              userType={userType}
              userName={userName}
              closeMobileMenu={() => {
                closeMobileMenu()
                closeLangSwitch()
              }}
            />

            {isLoggedIn && (
              <button
                className="header__toggler"
                aria-controls="a11y-main-menu-collapse"
                aria-expanded={isMenuOpen}
                onClick={() => toggleMobileMenu()}
              >
                <span className="sr-text">
                  {i18next.t('a11y:toggleMenu', {
                    toggle: isMenuOpen
                      ? i18next.t('common:open')
                      : i18next.t('common:close'),
                  })}
                </span>
                <span aria-hidden="true" />
                <span aria-hidden="true" />
                <span aria-hidden="true" />
                <span aria-hidden="true" />
              </button>
            )}
          </div>

          {isLoggedIn && (
            <>
              <nav
                className="header__nav nav--header"
                id="a11y-main-menu-collapse"
                aria-hidden={!isMenuOpen}
                aria-label={i18next.t('a11y:navLabelMain')}
              >
                <div className="nav__section nav__section--items">
                  <ul className="nav__items">
                    {[
                      {
                        Title: 'components',
                        Url: routeUpdaters(i18next.language).componentOverview
                          .url,
                      },
                      {
                        Title: 'dishes',
                        Url: routeUpdaters(i18next.language).dishesOverview.url,
                      },
                      {
                        Title: 'knowledge',
                        Url: routeUpdaters(i18next.language).knowledgeOverview
                          .url,
                      },
                      {
                        Title: 'blog',
                        Url: routeUpdaters(i18next.language).blogOverview.url,
                      },
                      {
                        Title: 'calendar',
                        Url: routeUpdaters(i18next.language).seasonalCalendar
                          .url,
                      },
                    ]
                      .concat(
                        isStudent
                          ? [
                              {
                                Title: 'student',
                                Url: i18next.t('header:studentUrl'),
                              },
                            ]
                          : isTeacher
                          ? [
                              {
                                Title: 'teacher',
                                Url: i18next.t('header:teacherUrl'),
                              },
                            ]
                          : []
                      )
                      .map(
                        (item) =>
                          item && (
                            <li
                              key={slugify(item.Title)}
                              className={`nav__item`}
                            >
                              {link(
                                linkWrapper({
                                  title: i18next.t(
                                    `header:${item.Title}`
                                  ),
                                  url: item.Url,
                                }),
                                {
                                  classname: `nav__link${
                                    item.Url === window.location.pathname
                                      ? ' nav__link--active'
                                      : ''
                                  }`,
                                },
                                i18next.t(`header:${item.Title}`),
                                () => {
                                  closeMobileMenu()
                                  closeLangSwitch()
                                }
                              )}
                            </li>
                          )
                      )}

                    {/* Language Switch */}
                    <li
                      key="lang-switch"
                      className="nav__item nav__item--expand expand expand--lang-switch data-hj-suppress"
                    >
                      <LanguageSwitch
                        isExpanded={languageSwitchMode == 'open'}
                        onExpand={() => {
                          setLanguageSwitchMode(
                            languageSwitchMode == 'open' ? 'closed' : 'open'
                          )
                          languageSwitchMode == 'open'
                            ? closeMobileMenu()
                            : null
                          languageSwitchMode == 'open'
                            ? closeLangSwitch()
                            : null
                        }}
                        onLanguageChanged={() => props.onLanguageChanged()}
                      />
                    </li>
                  </ul>
                </div>
              </nav>

              {/* Search */}
              {showSearchBar && (
                <SearchBarLayout
                  extraClasses="header__search"
                  searchString={searchString}
                  setSearchString={setSearchString}
                  searchTab={searchCategory}
                  onSubmit={() => {
                    closeMobileMenu()
                    closeLangSwitch()
                  }}
                />
              )}
            </>
          )}
          
          {!isLoggedIn && (
            <>
              <nav
                className="header__nav nav--header"
                id="a11y-main-menu-collapse"
                aria-hidden={!isMenuOpen}
                aria-label={i18next.t('a11y:navLabelMain')}
              >
                <div className="nav__section nav__section--items">
                  <ul className="nav__items">
                    {/* Language Switch */}
                    <li
                      key="lang-switch"
                      className="nav__item nav__item--expand expand expand--lang-switch data-hj-suppress"
                    >
                      <LanguageSwitch
                        isExpanded={languageSwitchMode == 'open'}
                        onExpand={() => {
                          setLanguageSwitchMode(
                            languageSwitchMode == 'open' ? 'closed' : 'open'
                          )
                          languageSwitchMode == 'open'
                            ? closeMobileMenu()
                            : null
                          languageSwitchMode == 'open'
                            ? closeLangSwitch()
                            : null
                        }}
                        onLanguageChanged={() => props.onLanguageChanged()}
                      />
                    </li>
                  </ul>
                </div>
              </nav>
            </>
          )}

          <HeaderButtons
            screenSize="desktop"
            isLoggedIn={isLoggedIn}
            userType={userType}
            userName={userName}
            closeMobileMenu={() => {
              closeMobileMenu()
              closeLangSwitch()
            }}
          />
        </div>
      </InView>
    </>
  )
}

// ----- Skiplinks

const SkipLinks = () => (
  <>
    <a href="#a11y-main" className="sr-text">
      {i18next.t('a11y:skipToContent')}
    </a>
    <a href="#a11y-footer" className="sr-text">
      {i18next.t('a11y:skipToFooter')}
    </a>
  </>
)

// ----- Language Switch

type LanguageSwitchProps = {
  isExpanded: boolean
  onExpand: () => void
  onLanguageChanged: () => void
}
export const LanguageSwitch = (p: LanguageSwitchProps) => (
  <>
    <button
      type="button"
      id="a11y-lang-switch-button"
      onClick={p.onExpand}
      title={i18next.t('header:changeLanguage')}
      className="expand__button text--s"
      aria-controls="a11y-lang-switch-collapse"
      aria-expanded={p.isExpanded}
    >
      <span className="sr-text">{`${i18next.t(
        'header:currentLanguage'
      )}: `}</span>
      <span className="button__label">
        <abbr
          title={i18next.t(
            `common:lang${i18next.language.toUpperCase()}Long`
          )}
        >
          {i18next.t(
            `common:lang${i18next.language.toUpperCase()}Short`
          )}
        </abbr>
      </span>
      <SVGIcon className="expand__toggle" size={10} iconId="chevron-down" />
    </button>
    <div
      id="a11y-lang-switch-collapse"
      aria-hidden={!p.isExpanded}
      className="expand__sub"
    >
      <ul>
        {['nl', 'en']
          .filter((lang) => lang !== i18next.language)
          .map((lang) => (
            <li key={lang}>
              <Link
                to={`/${lang}`}
                onClick={() => {
                  i18next.changeLanguage(lang)
                  document.documentElement.lang = lang
                  p.onExpand()
                  p.onLanguageChanged()
                }}
              >
                <span className="sr-text">{`${i18next.t(
                  'header:changeLanguage'
                )}: `}</span>
                <span>
                  <abbr
                    title={i18next.t(
                      `common:lang${lang.toUpperCase()}Long`
                    )}
                  >
                    {i18next.t(`common:lang${lang.toUpperCase()}Short`)}
                  </abbr>
                </span>
              </Link>
            </li>
          ))}
      </ul>
    </div>
  </>
)

// ----- Header Buttons

type HeaderButtonProps = {
  screenSize: 'mobile' | 'desktop'
  isLoggedIn: boolean
  userName: string
  userType: UserType
  closeMobileMenu: () => void
}
const HeaderButtons = (p: HeaderButtonProps) => {
  const isPro = p.userType == 'pro'
  const isStudent = p.userType == 'student'
  const isTeacher = p.userType == 'teacher'
  return (
    <div
      className={`header__buttons header__buttons--${p.screenSize} ${
        p.isLoggedIn ? 'data-hj-suppress' : ''
      }`}
      aria-hidden={p.screenSize === 'mobile'}
    >
      {p.isLoggedIn ? (
        <ButtonGhost
          kind="link"
          to={routeUpdaters(i18next.language).dashboard.url}
          onClick={() => p.closeMobileMenu()}
          size="s"
          title={i18next.t('header:navigateToDashboard')}
          label={
            <>
              <span className="sr-text">
                {i18next.t('header:navigateToDashboard')}
              </span>
              <span aria-hidden="true">{p.userName}</span>
            </>
          }
          iconId="user"
          iconSize="24px"
          extraClasses="header__button header__button--user hide-label"
        />
      ) : (
        <ButtonGhost
          kind="link"
          to={
            routeUpdaters(i18next.language).login.url +
            `?originPath=${window.location.pathname}`
          }
          onClick={() => {
            p.closeMobileMenu()
            DataLayer.headerButtonsClick(i18next.t('auth:loginHeader'))
          }}
          size="s"
          label={i18next.t('auth:loginHeader')}
          extraClasses="header__button"
        />
      )}

      {p.isLoggedIn ? (
        !(p.userType === 'unknown') &&
        !isPro &&
        !isStudent &&
        !isTeacher && (
          <ButtonPrimary
            kind="link"
            to={routeUpdaters(i18next.language).upgrade.url}
            onClick={() => {
              p.closeMobileMenu()
              DataLayer.headerButtonsClick(
                i18next.t('subscription:upgradeAccount')
              )
            }}
            size="s"
            label={i18next.t('subscription:upgradeAccount')}
            extraClasses="header__button"
          />
        )
      ) : (
        <StartTrialButton
          size="s"
          extraClasses="header__button hide--768px-down"
        />
      )}
    </div>
  )
}
